/**
 * Authentication Service
 * 
 * API service for user authentication
 */

import { apiClient } from './apiService';

const TOKEN_KEY = 'ai_crawler_token';
const USER_KEY = 'ai_crawler_user';

/**
 * Login with email and password
 */
export const login = async (email, password, rememberMe = false) => {
  try {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
      remember_me: rememberMe
    });
    
    const { access_token, user } = response.data;
    
    // Store token and user in localStorage
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    
    // Set default authorization header
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    return { user, token: access_token };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Register a new user
 */
export const register = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register', userData);
    
    const { access_token, user } = response.data;
    
    // Store token and user in localStorage
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    
    // Set default authorization header
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    return { user, token: access_token };
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Logout
 */
export const logout = async () => {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Always clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

/**
 * Get current user from token
 */
export const getCurrentAuthUser = async () => {
  try {
    const token = getToken();
    if (!token) return null;
    
    const response = await apiClient.get('/auth/me', {
      params: { token }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error getting current user:', error);
    // Token might be invalid, clear it
    logout();
    return null;
  }
};

/**
 * Get stored token
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Initialize auth (set token in headers if exists)
 */
export const initAuth = () => {
  const token = getToken();
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

